import { Injectable, inject } from '@angular/core'
import { OrganizationService } from '@se-po/shared-data-access-services/organization'
import { CentralServicePolicyCheckService } from 'se-fe-data-access/central-service-policy-check'
import type {
  CentralServicePolicyCheckAction,
  CentralServicePolicyCheckResult,
} from 'se-fe-data-access/central-service-policy-check'
import { Observable, finalize, map, of, shareReplay, switchMap } from 'rxjs'
import { DENIED_PROFILE_ACCESS, ProfileAccess, ProfileAccessOrganization } from './profile-access.types'

const HQ_PERMISSION_ACTIONS = [
  'access',
  'admin_management',
  'dibs',
  'memberships',
  'power_pay',
  'profiles',
  'roster_history',
]

const HQ_PROFILE_ACTIONS = [
  'buy_memberships',
  'message',
]

interface OrganizationContext {
  id: string
  rootOrganizationIds: Array<string | number>
}

@Injectable({ providedIn: 'root' })
export class ProfileAccessService {
  private readonly organizationService = inject(OrganizationService)
  private readonly policyCheckService = inject(CentralServicePolicyCheckService)
  private readonly cache = new Map<string, Observable<ProfileAccess>>()

  public resolve(
    orgId: string | number,
    organization?: ProfileAccessOrganization
  ): Observable<ProfileAccess> {
    const key = String(orgId)
    const cached = this.cache.get(key)
    if (cached) return cached

    const access$ = this.organizationContext(key, organization).pipe(
      switchMap(context => this.policyCheckService.findAll({
        resources: [
          {
            resource: { type: 'hq_permissions', id: context.id },
            actions: HQ_PERMISSION_ACTIONS,
            context: {
              root_organization_ids: context.rootOrganizationIds,
            },
          },
          {
            resource: { type: 'hq_profiles', id: context.id },
            actions: HQ_PROFILE_ACTIONS,
          },
        ],
      })),
      map(results => this.profileAccess(results)),
      finalize(() => {
        if (this.cache.get(key) === access$) this.cache.delete(key)
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    )

    this.cache.set(key, access$)
    return access$
  }

  private organizationContext(
    orgId: string,
    organization?: ProfileAccessOrganization
  ): Observable<OrganizationContext> {
    if (organization && 'root_organizations' in organization) {
      return of({
        id: orgId,
        rootOrganizationIds: (organization.root_organizations || []).map(root => root.id),
      })
    }

    return this.organizationService.findPublic(orgId, { include: 'root_organizations' }).pipe(
      map(result => ({
        id: orgId,
        rootOrganizationIds: (result?.root_organizations || []).map(root => root.id),
      }))
    )
  }

  private profileAccess(results: CentralServicePolicyCheckResult[]): ProfileAccess {
    const hqPermissions = results.find(result => result.resource.type === 'hq_permissions')?.actions
    const hqProfiles = results.find(result => result.resource.type === 'hq_profiles')?.actions
    if (!hqPermissions || !this.actionAllowed(hqPermissions.access)) return DENIED_PROFILE_ACCESS

    const profiles = hqPermissions.profiles
    const memberships = hqPermissions.memberships
    const powerPay = hqPermissions.power_pay

    return {
      admins: this.visibleOrUsable(hqPermissions.admin_management),
      dibs: this.actionFlag(hqPermissions.dibs, 'visible'),
      financials: this.actionFlag(powerPay, 'visible') && this.actionFlag(powerPay, 'usable'),
      memberships: this.visibleOrUsable(memberships) ||
        this.actionAllowed(hqProfiles?.buy_memberships),
      messaging: this.actionFlag(profiles, 'messageable') || this.actionAllowed(hqProfiles?.message),
      profiles: this.actionFlag(profiles, 'usable'),
      rosterHistory: this.visibleOrUsable(hqPermissions.roster_history),
    }
  }

  private visibleOrUsable(action: CentralServicePolicyCheckAction | undefined): boolean {
    return this.actionFlag(action, 'visible') || this.actionFlag(action, 'usable')
  }

  private actionAllowed(action: CentralServicePolicyCheckAction | undefined): boolean {
    return typeof action === 'boolean' ? action : false
  }

  private actionFlag(action: CentralServicePolicyCheckAction | undefined, flag: string): boolean {
    if (typeof action === 'boolean') return action
    return !!action?.[flag]
  }
}
